/**
 * Portal layout route — /portal/*
 * Guard: role ∈ {student, parent}. Redirects to /login if unauthenticated, /403 if wrong role.
 * Layout: topbar (brand + child-switcher for parent + user menu) + bottom nav (mobile) + Outlet.
 * Mobile-first: no sidebar — bottom nav on mobile, top nav links on desktop.
 */
import { createFileRoute, Outlet, Link, useNavigate, useLocation } from '@tanstack/react-router';
import {
  BookOpen,
  Calendar,
  ChartLine,
  ClipboardList,
  Home,
  LogOut,
  Settings,
  User,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/client/components/theme-toggle';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { authClient } from '@client/lib/auth-client';
import { ChildSwitcher } from '@client/components/portal/child-switcher';

export const Route = createFileRoute('/portal')({
  beforeLoad: async ({ navigate }) => {
    const { data: session } = await authClient.getSession();
    if (!session?.user) {
      throw navigate({ to: '/login', search: { redirect: '/portal' }, replace: true });
    }
    const role = (session.user as { role?: string }).role ?? '';
    if (role !== 'student' && role !== 'parent') {
      throw navigate({ to: '/403', replace: true });
    }
    return { session };
  },
  component: PortalLayout,
});

const NAV_ITEMS = [
  { to: '/portal', label: 'Tổng quan', icon: Home, exact: true },
  { to: '/portal/schedule', label: 'Lịch học', icon: Calendar, exact: false },
  { to: '/portal/grades', label: 'Điểm số', icon: ChartLine, exact: false },
  { to: '/portal/progress', label: 'Tiến độ', icon: BookOpen, exact: false },
  { to: '/portal/assignments', label: 'Bài tập', icon: ClipboardList, exact: false },
];

function PortalLayout() {
  const navigate = useNavigate();
  const { session } = Route.useRouteContext();
  const location = useLocation();
  const user = session?.user as { name?: string; email?: string; role?: string } | undefined;
  const role = (user?.role ?? '') as string;
  const fullName = user?.name ?? user?.email ?? 'Học viên';

  async function handleLogout() {
    await authClient.signOut();
    void navigate({ to: '/login', replace: true });
  }

  function isActive(item: (typeof NAV_ITEMS)[0]) {
    return item.exact
      ? location.pathname === item.to
      : location.pathname.startsWith(item.to);
  }

  return (
    <div className="flex min-h-screen flex-col bg-background overflow-x-hidden">
      {/* Topbar */}
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-2 border-b border-border bg-background/95 backdrop-blur px-3 md:px-4">
        {/* Brand */}
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-base font-bold text-primary">CLC</span>
          <span className="text-xs text-muted-foreground hidden sm:block">
            {role === 'parent' ? 'Phụ huynh' : 'Học viên'}
          </span>
        </div>

        {/* Child switcher — only for parent, center on mobile */}
        {role === 'parent' && (
          <div className="flex-1 max-w-xs mx-2">
            <ChildSwitcher />
          </div>
        )}

        {/* Desktop nav links */}
        <nav className="hidden md:flex items-center gap-1">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors min-h-[36px]
                ${isActive(item)
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-1 shrink-0">
        <ThemeToggle />
        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="flex items-center gap-2 h-9 px-2 shrink-0">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold shrink-0">
                {fullName.charAt(0).toUpperCase()}
              </div>
              <span className="hidden sm:block text-sm truncate max-w-[100px]">{fullName}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <div className="px-2 py-1.5">
              <p className="text-xs font-medium truncate">{fullName}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <a href="/account" className="flex items-center gap-2 cursor-pointer">
                <User className="h-4 w-4" />
                Hồ sơ
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a href="/account" className="flex items-center gap-2 cursor-pointer">
                <Settings className="h-4 w-4" />
                Cài đặt
              </a>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive flex items-center gap-2 cursor-pointer"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4" />
              Đăng xuất
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </header>

      {/* Page content */}
      <main className="flex-1 p-4 md:p-6 min-w-0 pb-20 md:pb-6">
        <Outlet />
      </main>

      {/* Bottom nav — mobile only */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-border bg-background/95 backdrop-blur">
        <div className="flex items-center justify-around h-14">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`flex flex-col items-center justify-center gap-0.5 min-w-[44px] min-h-[44px] px-2 rounded-md transition-colors
                ${isActive(item) ? 'text-primary' : 'text-muted-foreground'}`}
            >
              <item.icon className="h-5 w-5 shrink-0" />
              <span className="text-[10px] leading-none">{item.label}</span>
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
}
